import type { AxiosError } from "axios";
import { Sentry } from "./sentry";

/**
 * Reporta a Sentry solo los errores HTTP que son realmente bugs:
 *   - 5xx → bug del backend (o un edge case que rompió)
 *   - network error (sin response) → conectividad / CORS / timeout
 *
 * No reporta:
 *   - 401 → parte del flujo de auth (el interceptor intenta refresh)
 *   - 4xx (resto) → errores esperables de validación/permiso; el toast
 *     en UI ya los comunica. Sentry igual los breadcrumb-ea automáticamente
 *     vía la integración XHR/fetch, así que aparecen en el contexto del
 *     siguiente error real.
 *
 * Incluye request body + response body completos para debugging profundo.
 * NOTA: esto puede mandar PII (passwords, datos de clientes, datos clínicos)
 * a Sentry. Cuando armemos el `beforeSend` con scrubbing de campos
 * sensibles, este es el lugar a cuidar.
 */
export function reportAxiosError(error: AxiosError): void {
  const status = error.response?.status;
  if (status === 401) return;

  const isServerError = !status || status >= 500;
  if (!isServerError) return;

  const method = error.config?.method?.toUpperCase() ?? "UNKNOWN";
  const url = sanitizeUrl(error.config?.url);

  Sentry.captureException(error, {
    tags: {
      source: "axios",
      http_status: String(status ?? "network"),
      http_method: method,
    },
    extra: {
      url,
      response_data: error.response?.data,
      request_data: parseRequestBody(error.config?.data),
    },
  });
}

function sanitizeUrl(url: string | undefined): string {
  if (!url) return "unknown";
  return url.split("?")[0];
}

function parseRequestBody(data: unknown): unknown {
  if (data == null) return undefined;
  if (data instanceof FormData) return "[FormData]";
  if (typeof data === "string") {
    try {
      return JSON.parse(data);
    } catch {
      return data;
    }
  }
  return data;
}
